import { AxiosError } from 'axios';

/**
 * Extract a human-readable error message from an Axios error response.
 *
 * Handles the two shapes the backend uses:
 *   - `{ errors: ["Hexa HQ SKU has already been taken", ...] }`  (Rails validation)
 *   - `{ error: "Something went wrong" }`                        (general error)
 *
 * Falls back to the Axios error message or a generic string.
 */
export function extractApiError(
  error: unknown,
  fallback = 'An unexpected error occurred',
): string {
  if (!error) return fallback;

  const axiosError = error as AxiosError<Record<string, unknown>>;
  const data = axiosError.response?.data;

  if (data) {
    // Rails validation: { errors: ["..."] }
    if (Array.isArray(data.errors) && data.errors.length > 0) {
      return data.errors.join('. ');
    }

    // General error: { error: "..." }
    if (typeof data.error === 'string' && data.error.length > 0) {
      return data.error;
    }
  }

  // Network / timeout errors from Axios
  if (axiosError.message) {
    return axiosError.message;
  }

  return fallback;
}

export interface ApiErrorInfo {
  message: string;
  /** Machine-readable failure reason from the backend (e.g. 'outage', 'staging'). */
  reason?: string;
  /** True when the backend marks this refusal as one a rep may override (sc-6006). */
  overridable: boolean;
}

/**
 * Like `extractApiError`, but also surfaces the `reason` / `overridable` fields
 * the digest approval gate attaches to a 422 so the UI can decide whether to
 * offer an "Approve Anyway" override modal (sc-6006).
 */
export function extractApiErrorInfo(
  error: unknown,
  fallback = 'An unexpected error occurred',
): ApiErrorInfo {
  const message = extractApiError(error, fallback);
  const data = (error as AxiosError<Record<string, unknown>>)?.response?.data;
  const reason = typeof data?.reason === 'string' ? data.reason : undefined;
  const overridable = data?.overridable === true;
  return { message, reason, overridable };
}
